import { basePath } from './runtimeConfig'

/**
 * The React Router basename for the current request.
 *
 * It has to match the path the BROWSER is on, which is not always the path the pod served
 * (FAAS-909). This app is reachable at two public prefixes during the Portal migration:
 *
 *  - `/landing-ms/*` directly, which is what Istio routes and what assets are served under.
 *  - `/`, the real giffgaff homepage, which Portal's Apache rule proxies to `/landing-ms/`.
 *
 * A proxy is invisible to the browser, so on the second one `window.location.pathname` is
 * `/` while Modern.js's `server.baseUrl` still stripped `/landing-ms` server-side. Handing
 * React Router a fixed `/landing-ms` basename there means the current location does not
 * start with the basename, the router matches nothing, and the page blanks on hydration
 * after having rendered correctly from SSR.
 *
 * On the server there is no `window`, so this always returns `basePath`, which is what SSR
 * needs because Modern.js matches against the internal path.
 *
 * ponytail: safe while no route reachable at `/` renders a react-router `<Link>`. The only
 * `<Link>` in this app today is in SimOnlyDeals' PlanTabs, which is not. Adding one to the
 * homepage means SSR would emit `/landing-ms/...` hrefs while the client expects bare ones,
 * so the public prefix would then have to be threaded through `window.__RUNTIME_CONFIG__`
 * and set from the request on the server.
 */
export const resolveRouterBasename = (pathname?: string): string | undefined => {
  if (!basePath) {
    return undefined
  }

  // Server render, or any caller without a location: the internal path is the right answer.
  if (pathname === undefined) {
    return basePath
  }

  // `startsWith(basePath)` alone would also match `/landing-msomething`. Not reachable
  // through Istio today, but the stricter test is the same length.
  const onBasePath = pathname === basePath || pathname.startsWith(`${basePath}/`)

  return onBasePath ? basePath : undefined
}

/** The basename for the running environment. `undefined` off the browser is the server case. */
export const currentRouterBasename = (): string | undefined =>
  resolveRouterBasename(typeof window === 'undefined' ? undefined : window.location.pathname)
